import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCrmTenant } from '../crm/useCrmTenant';
import { CircularProgress } from '@mui/material';
import { kycApi } from '../../services';
import {
  CompletedKycDetails,
  KycFieldStatus,
  KycSubmissionContext,
} from '../form-renderer/KycSubmissionContext';
import CustomButton from './Button';
import { AppDialog, AppDialogSpinner } from '../shared/AppDialog';
import {
  FieldBlock,
  FieldLabel,
  FieldInput,
  FieldError,
} from '../shared/AppDialogField';
import { IdCardIcon } from '../shared/Icons';
import { DateInput } from './DateTimeInput';

interface KycButtonData {
  content?: React.ReactNode;
  field_name?: string;
  data?: {
    className?: string;
    field_name?: string;
    isRequired?: boolean;
    type?: string;
    onSubmitCallback?: (() => void) | string;
    href?: string;
    openInNewTab?: boolean | string;
    preparePayment?: () => Promise<{
      leadId: string;
      formId: string;
      name: string;
      email: string;
      phone: string;
    }>;
    [key: string]: any;
  };
}

interface KycButtonProps {
  data: KycButtonData;
}

// Convert an HTML date input value (YYYY-MM-DD) to the DD-MM-YYYY format
// the CVL KRA microservice expects.
const toDobOrIncorp = (isoDate: string): string => {
  const [year, month, day] = isoDate.split('-');
  return `${day}-${month}-${year}`;
};

const KycButton = React.forwardRef<
  HTMLButtonElement,
  KycButtonProps
>((props, ref) => {
  const { tenantId } = useParams<{ tenantId?: string }>();
  // Gateway calls use the shared CRM tenant from the form URL.
  const crmTenantId = useCrmTenant();
  const { data, content } = props.data;
  const kycSubmission = useContext(KycSubmissionContext);

  const fieldName =
    data?.field_name || props.data.field_name || 'kyc';
  const storageKey = `formKyc:${tenantId || 'unknown'}:${fieldName}:done`;
  const metaKey = `formKycMeta:${tenantId || 'unknown'}:${fieldName}`;

  const [localStatus, setLocalStatus] =
    useState<KycFieldStatus>(
      sessionStorage.getItem(storageKey) === '1'
        ? 'done'
        : 'idle',
    );
  const [localErrorMessage, setLocalErrorMessage] =
    useState('');
  const externalStatus =
    kycSubmission?.getKycStatus?.(fieldName);
  const status = externalStatus?.status ?? localStatus;
  const errorMessage =
    externalStatus?.errorMessage ?? localErrorMessage;

  const updateStatus = (
    next: KycFieldStatus,
    message: string = '',
  ) => {
    setLocalStatus(next);
    setLocalErrorMessage(message);
    kycSubmission?.setKycStatus?.(fieldName, {
      status: next,
      errorMessage: message,
    });
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [pan, setPan] = useState('');
  const [dob, setDob] = useState('');
  // Which of PAN/DOB the dialog still needs to ask for - fields already
  // resolved from the form aren't shown, so the user isn't asked twice.
  const [dialogFields, setDialogFields] = useState<
    Array<'pan' | 'dob'>
  >(['pan', 'dob']);
  const actionButtonRef = useRef<HTMLButtonElement>(null);
  const prevStatusRef = useRef<KycFieldStatus>(status);

  useEffect(() => {
    if (
      status === 'done' &&
      prevStatusRef.current !== 'done'
    ) {
      actionButtonRef.current?.click();
    }
    prevStatusRef.current = status;
  }, [status]);

  const verifyKyc = async (
    panOverride?: string,
    dobOverride?: string,
  ) => {
    const usePan = panOverride ?? pan;
    const useDob = dobOverride ?? dob;
    if (!crmTenantId) {
      updateStatus(
        'error',
        'Missing tenant context for KYC.',
      );
      return;
    }
    if (!usePan || !useDob) {
      updateStatus(
        'error',
        'PAN and date of birth are required.',
      );
      return;
    }

    updateStatus('processing');

    try {
      const prepareLead =
        data?.preparePayment || kycSubmission?.prepareLead;
      if (!prepareLead) {
        throw new Error(
          'KYC is unavailable in the builder preview. Save the form and open its View page to test KYC.',
        );
      }
      const customer = await prepareLead();

      const checkResult = await kycApi.checkKyc(crmTenantId, {
        pan: usePan,
        leadId: customer.leadId,
      });

      if (!checkResult?.isValid) {
        updateStatus(
          'error',
          checkResult?.message ||
            'PAN verification failed.',
        );
        return;
      }

      await kycApi.fetchPanDetails(crmTenantId, {
        panNo: usePan,
        dobOrIncorp: toDobOrIncorp(useDob),
        leadId: customer.leadId,
      });

      sessionStorage.setItem(storageKey, '1');
      const completedKyc: CompletedKycDetails = {
        fieldName,
        leadId: customer.leadId,
      };
      sessionStorage.setItem(
        metaKey,
        JSON.stringify(completedKyc),
      );
      setDialogOpen(false);
      await kycSubmission?.completeKyc(
        fieldName,
        completedKyc,
      );
      updateStatus('done');
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'KYC verification failed.';
      updateStatus('error', String(message));
    }
  };

  // A DD-MM-YYYY value (as produced by toDobOrIncorp / some form fields) needs
  // converting to YYYY-MM-DD for the <input type="date"> value; anything else
  // unrecognized is left for the user to fill in rather than guessed at.
  const normalizeDobForInput = (
    value: string,
  ): string | undefined => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const ddmmyyyy = value.match(
      /^(\d{2})-(\d{2})-(\d{4})$/,
    );
    if (ddmmyyyy) {
      const [, day, month, year] = ddmmyyyy;
      return `${year}-${month}-${day}`;
    }
    return undefined;
  };

  // Mirrors the payment section's "complete your details" dialog: reuse
  // PAN/DOB already captured by a regular field on this form instead of
  // asking the user to type them again, only prompting for what's missing.
  const openVerifyDialog = () => {
    const foundPan = kycSubmission?.getFieldValue?.([
      'pan',
    ]);
    const foundDobRaw = kycSubmission?.getFieldValue?.([
      'dob',
    ]);
    const foundDob = foundDobRaw
      ? normalizeDobForInput(foundDobRaw)
      : undefined;
    const normalizedPan = foundPan
      ? foundPan.toUpperCase().trim()
      : '';

    setPan(normalizedPan);
    setDob(foundDob || '');

    if (normalizedPan && foundDob) {
      verifyKyc(normalizedPan, foundDob);
      return;
    }

    const missing: Array<'pan' | 'dob'> = [];
    if (!normalizedPan) missing.push('pan');
    if (!foundDob) missing.push('dob');
    setDialogFields(missing);
    setDialogOpen(true);
  };

  const hiddenActionButton = (
    <div style={{ display: 'none' }}>
      <CustomButton
        ref={actionButtonRef}
        data={{
          data: {
            type: data?.type,
            onSubmitCallback: data?.onSubmitCallback,
            href: data?.href,
            openInNewTab: data?.openInNewTab,
            submissionResponse: (data as any)
              ?.submissionResponse,
          } as any,
          content: '',
        }}
      />
    </div>
  );

  if (status === 'done') {
    return (
      <div
        className={data?.className || 'btn d-flex'}
        style={{
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        <span>Your KYC is verified.</span>
        <CustomButton
          data={{
            className: 'btn d-flex',
            onClick: () => actionButtonRef.current?.click(),
            data: { type: 'button' },
            content: 'Continue',
          }}
        />
        {hiddenActionButton}
      </div>
    );
  }

  const buttonLabel =
    status === 'processing'
      ? 'Checking...'
      : typeof content === 'string'
        ? content
        : content || 'Verify KYC';

  return (
    <div>
      <CustomButton
        ref={ref}
        data={{
          className: data?.className || 'btn d-flex ',
          onClick: openVerifyDialog,
          data: { type: 'button' },
          content:
            status === 'processing' ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <CircularProgress
                  size={14}
                  thickness={5}
                  sx={{ color: 'inherit' }}
                />
                {buttonLabel}
              </span>
            ) : (
              buttonLabel
            ),
        }}
      />
      {hiddenActionButton}
      {errorMessage && status !== 'processing' && (
        <div
          style={{
            color: '#d32f2f',
            fontSize: '0.85rem',
            marginTop: 4,
          }}
        >
          {errorMessage}
        </div>
      )}

      <AppDialog
        open={dialogOpen}
        onClose={() => {
          if (status !== 'processing') setDialogOpen(false);
        }}
        icon={<IdCardIcon />}
        title="Verify KYC"
        subtitle="We need a few details to confirm your identity before continuing."
        actions={[
          {
            label: 'Cancel',
            variant: 'secondary',
            onClick: () => setDialogOpen(false),
            disabled: status === 'processing',
          },
          {
            label: status === 'processing' ? 'Verifying…' : 'Verify',
            onClick: () => verifyKyc(),
            disabled: status === 'processing',
            icon: status === 'processing' ? <AppDialogSpinner /> : undefined,
          },
        ]}
      >
        {dialogFields.includes('pan') && (
          <FieldBlock>
            <FieldLabel htmlFor="kyc-pan">PAN</FieldLabel>
            <FieldInput
              id="kyc-pan"
              autoFocus
              value={pan}
              onChange={(e) => setPan(e.target.value.toUpperCase())}
              disabled={status === 'processing'}
            />
          </FieldBlock>
        )}
        {dialogFields.includes('dob') && (
          <FieldBlock>
            <FieldLabel htmlFor="kyc-dob">Date of Birth</FieldLabel>
            <DateInput
              data={{
                field_name: 'kyc-dob',
                value: dob,
              }}
              onChange={(vals) => setDob(vals['kyc-dob'])}
              read_only={status === 'processing'}
            />
          </FieldBlock>
        )}
        {errorMessage && <FieldError>{errorMessage}</FieldError>}
      </AppDialog>
    </div>
  );
});

KycButton.displayName = 'KycButton';

export default KycButton;
